import {
  rumiGetLatestRumiRunOptions,
  rumiStartRumiRunMutation,
  useHeyApi,
} from "@platform/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { RumiNullableRumiRun } from "@platform/api";
import type { AxiosError } from "axios";

/**
 * How often the Launchpad re-reads an open Run, so it shows the end within
 * ten seconds of it. Runs last up to an hour.
 */
const OPEN_RUN_POLL_MS = 10_000;

export type RumiRunState =
  | { kind: "loading" }
  /** The caller's role may not use Rumi. */
  | { kind: "notAvailable" }
  | { kind: "loadFailed" }
  | { kind: "ready"; run: RumiNullableRumiRun };

const statusOf = (error: unknown): number | undefined =>
  (error as AxiosError | null)?.response?.status;

/**
 * The RFP's latest Rumi Run, and a way to start the next one. `rfpId` is the
 * numeric RFP id in the URL.
 */
export const useRumiRun = (
  rfpId: string,
): {
  state: RumiRunState;
  start: () => void;
  /** True when the last start failed for a reason other than an open Run. */
  startFailed: boolean;
} => {
  const { centroPortalClient } = useHeyApi();
  const queryClient = useQueryClient();

  const latest = rumiGetLatestRumiRunOptions({
    path: { id: rfpId },
    client: centroPortalClient,
  });

  // The cached Run is reset, not just re-read, when the start leaves it
  // stale: the read either shows the Run that is open now or fails, and an
  // earlier Run never stays on screen as the current one.
  const showOpenRun = (): Promise<void> =>
    queryClient.resetQueries({ queryKey: latest.queryKey });

  const mutation = useMutation({
    ...rumiStartRumiRunMutation({ client: centroPortalClient }),
    // A read sent before the start could land after it with the previous
    // Run, so it is cancelled before the started Run is written.
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: latest.queryKey });

      if (data.run) {
        queryClient.setQueryData(latest.queryKey, data);
      } else {
        await showOpenRun();
      }
    },
    // 409: a Run is already open, started from another tab or by assignment.
    onError: (error) => {
      if (statusOf(error) === 409) {
        void showOpenRun();
      }
    },
  });

  // A 403 on the read or the start: the caller's role may not use Rumi.
  const refusedOnStart = statusOf(mutation.error) === 403;

  const query = useQuery({
    ...latest,
    refetchInterval: (current) =>
      current.state.data?.run?.open &&
      statusOf(current.state.error) !== 403 &&
      !refusedOnStart
        ? OPEN_RUN_POLL_MS
        : false,
    // A 403 will not change on retry.
    retry: (failures, error) => statusOf(error) !== 403 && failures < 3,
  });

  // A 403 holds whatever is cached, since the caller may no longer see it.
  // Any other failed background refetch keeps the Run already shown.
  const state: RumiRunState =
    statusOf(query.error) === 403 || refusedOnStart
      ? { kind: "notAvailable" }
      : query.data
        ? { kind: "ready", run: query.data.run }
        : query.isError
          ? { kind: "loadFailed" }
          : { kind: "loading" };

  return {
    state,
    // A second click while the first start is in flight would only be refused.
    start: () => {
      if (!mutation.isPending) {
        mutation.mutate({ path: { id: rfpId } });
      }
    },
    startFailed:
      mutation.isError &&
      statusOf(mutation.error) !== 409 &&
      statusOf(mutation.error) !== 403,
  };
};
